import { Button, useToken } from "@chakra-ui/react";
import { motion, useReducedMotion } from "framer-motion";

const hexToRgba = (hex, alpha) => {
  if (!hex || hex.length < 4) return `rgba(59, 130, 246, ${alpha})`;
  const cleanHex = hex.startsWith("#") ? hex.slice(1) : hex;
  if (cleanHex.length === 3) {
    const r = parseInt(cleanHex[0] + cleanHex[0], 16);
    const g = parseInt(cleanHex[1] + cleanHex[1], 16);
    const b = parseInt(cleanHex[2] + cleanHex[2], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  if (cleanHex.length === 6) {
    const r = parseInt(cleanHex.slice(0, 2), 16);
    const g = parseInt(cleanHex.slice(2, 4), 16);
    const b = parseInt(cleanHex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `rgba(59, 130, 246, ${alpha})`;
};

/**
 * Liquid-glass button. Solid variants keep white text over the tinted fill;
 * quiet variants use a white translucent fill with the tint as ink.
 * Motion lives in Framer only, to avoid transform conflicts with Chakra.
 */
const GlassButton = ({
  children,
  colorScheme = "blue",
  size = "md",
  variant = "solid",
  ...rest
}) => {
  const reduceMotion = useReducedMotion();
  const isHex =
    typeof colorScheme === "string" &&
    colorScheme.startsWith("#") &&
    (colorScheme.length === 4 || colorScheme.length === 7);

  const [chakraBase, chakraHover, chakraActive, chakraInk] = useToken("colors", [
    !isHex ? `${colorScheme}.500` : "transparent",
    !isHex ? `${colorScheme}.400` : "transparent",
    !isHex ? `${colorScheme}.600` : "transparent",
    !isHex ? `${colorScheme}.700` : "transparent",
  ]);

  const baseColor = isHex ? colorScheme : chakraBase || "#3b82f6";
  const hoverColor = isHex ? colorScheme : chakraHover || baseColor;
  const activeColor = isHex ? colorScheme : chakraActive || baseColor;
  const inkColor = isHex ? "#2d3748" : chakraInk || baseColor;

  const sizeStyles =
    size === "sm"
      ? { px: 4, py: 2, fontSize: "sm", minH: "40px" }
      : size === "lg"
        ? { px: 8, py: 5, fontSize: "md", minH: "52px" }
        : {
            px: { base: 5, md: 6 },
            py: { base: 3, md: 4 },
            fontSize: { base: "sm", md: "md" },
            minH: "44px",
          };

  const isQuiet = variant === "outline" || variant === "ghost";

  const toneStyles = isQuiet
    ? {
        color: inkColor,
        bg:
          variant === "ghost"
            ? "rgba(255,255,255,0.58)"
            : "rgba(255,255,255,0.74)",
        borderColor: hexToRgba(baseColor, 0.7),
        boxShadow: "0 6px 18px rgba(2,6,23,0.14)",
        _hover: {
          bg: "rgba(255,255,255,0.92)",
          borderColor: hexToRgba(hoverColor, 1),
          boxShadow: `0 10px 24px ${hexToRgba(baseColor, 0.24)}`,
        },
        _active: { bg: hexToRgba(activeColor, 0.16) },
        fallbackBg: "rgba(255,255,255,0.95)",
      }
    : {
        color: "white",
        bg: hexToRgba(baseColor, 0.85),
        borderColor: "whiteAlpha.500",
        boxShadow: `0 8px 24px ${hexToRgba(baseColor, 0.35)}, inset 0 1px 0 rgba(255,255,255,0.35)`,
        _hover: {
          bg: hexToRgba(hoverColor, 0.95),
          borderColor: "whiteAlpha.700",
          boxShadow: `0 12px 28px ${hexToRgba(baseColor, 0.45)}, inset 0 1px 0 rgba(255,255,255,0.45)`,
        },
        _active: { bg: hexToRgba(activeColor, 0.78) },
        fallbackBg: hexToRgba(baseColor, 1),
      };

  return (
    <Button
      as={motion.button}
      whileHover={reduceMotion ? undefined : { y: -2, scale: 1.02 }}
      whileTap={reduceMotion ? undefined : { scale: 0.98 }}
      transition={{ type: "spring", stiffness: 380, damping: 22 }}
      position="relative"
      overflow="hidden"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      gap={2}
      fontWeight="bold"
      color={toneStyles.color}
      borderRadius="pill"
      zIndex={1}
      border={isQuiet ? "1.5px solid" : "1px solid"}
      backdropFilter="blur(14px) saturate(170%)"
      bg={toneStyles.bg}
      borderColor={toneStyles.borderColor}
      boxShadow={toneStyles.boxShadow}
      _hover={toneStyles._hover}
      _active={toneStyles._active}
      _focusVisible={{ boxShadow: "focus" }}
      _disabled={{ opacity: 0.55, cursor: "not-allowed" }}
      sx={{
        "@supports not ((-webkit-backdrop-filter: blur(1px)) or (backdrop-filter: blur(1px)))":
          {
            bg: toneStyles.fallbackBg,
          },
      }}
      {...sizeStyles}
      {...rest}
      variant="unstyled"
    >
      {children}
    </Button>
  );
};

export default GlassButton;
